import { useState } from "react";
import { Mail, Phone, MessageCircle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { api, formatApiError } from "../lib/api";
import { IMAGES, OWNER_EMAIL, WHATSAPP_NUMBER } from "../lib/constants";

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] rounded-none px-0 py-3 text-white placeholder:text-white/30 outline-none transition-colors";

export default function Contact() {
  const { t } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete the form.");
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-28 md:pt-36 pb-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-4">HRH · Concierge</p>
          <h1 className="font-serif font-light text-4xl md:text-6xl tracking-tight mb-4">{t.contact.title}</h1>
          <p className="text-white/50 mb-10">{t.contact.sub}</p>

          <form onSubmit={submit} className="space-y-8 max-w-lg" data-testid="contact-form">
            <input className={inputCls} placeholder={t.contact.name} data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input type="email" className={inputCls} placeholder={t.contact.email} data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className={inputCls} placeholder={t.contact.phone} data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <textarea rows={4} className={inputCls} placeholder={t.contact.message} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-fill border border-[var(--gold)] text-[var(--gold)] px-10 py-4 text-xs uppercase tracking-[0.2em] flex items-center gap-3 transition-colors duration-300">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t.contact.submit} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-12 pt-10 border-t border-white/10">
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-5">{t.contact.or}</p>
            <div className="space-y-4">
              <a href={`mailto:${OWNER_EMAIL}`} className="flex items-center gap-3 text-white/70 hover:text-[var(--gold)] transition-colors"><Mail className="w-4 h-4" /> {OWNER_EMAIL}</a>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/70 hover:text-[var(--gold)] transition-colors"><MessageCircle className="w-4 h-4" /> +52 56 3629 8441 (WhatsApp)</a>
              <a href={`tel:+${WHATSAPP_NUMBER}`} className="flex items-center gap-3 text-white/70 hover:text-[var(--gold)] transition-colors"><Phone className="w-4 h-4" /> +52 56 3629 8441</a>
            </div>
          </div>
        </div>

        <div className="relative hidden lg:block overflow-hidden">
          <img src={IMAGES.chauffeur2} alt="Concierge" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
        </div>
      </div>
    </div>
  );
}
